import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaDollarSign, FaPercentage, FaCube, FaTrophy } from 'react-icons/fa';
import config from '../../config';
import terms from '../../config/terminology';
import { formatCurrency } from '../../utils/formatCurrency';
import CastleVisualization from './CastleVisualization';

export default function PublicCastleView() {
  const { username } = useParams();
  const [castleData, setCastleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCastleData();
  }, [username]);

  const fetchCastleData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${config.API_URL}/creators/castle-public/${username}`);
      if (!res.ok) {
        if (res.status === 404) throw new Error('Creator not found');
        throw new Error('Failed to load castle');
      }
      const data = await res.json();
      setCastleData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const tierColor = (tier) => {
    switch (tier) {
      case 'title': return 'text-amber-400';
      case 'premium': return 'text-purple-400';
      default: return 'text-blue-400';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#14161f] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error || !castleData) {
    return (
      <div className="min-h-screen bg-[#14161f] text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">{error || 'Castle not found'}</h2>
          <Link to="/" className="text-cyan-400 hover:text-cyan-300 transition">Go home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#14161f] text-white">
      <div className="max-w-5xl mx-auto p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            {castleData.logo_url && (
              <img src={castleData.logo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
            )}
            <div>
              <h1 className="text-2xl font-bold">{castleData.creator_name}'s Castle</h1>
              <p className="text-gray-500 text-sm">@{castleData.creator_username}</p>
            </div>
          </div>
          {castleData.payments_enabled && (
            <Link
              to={`/${username}`}
              className="px-5 py-2.5 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-semibold text-sm transition"
            >
              Send a {terms.Donation}
            </Link>
          )}
        </div>

        {/* Weekly Note */}
        {castleData.weekly_note && (
          <div className="bg-[#1c1f2d] rounded-xl p-5 border border-gray-800/50 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📝</span>
              <h3 className="font-semibold text-sm">Weekly Note</h3>
            </div>
            <p className="text-sm text-gray-300">{castleData.weekly_note}</p>
          </div>
        )}

        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#1c1f2d] rounded-xl p-5 border border-gray-800/50">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-400 text-sm">Total Raised</p>
              <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                <FaDollarSign className="text-cyan-400" size={14} />
              </div>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(castleData.total_raised)}</p>
          </div>

          <div className="bg-[#1c1f2d] rounded-xl p-5 border border-gray-800/50">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-400 text-sm">Brickagy</p>
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <FaPercentage className="text-emerald-400" size={14} />
              </div>
            </div>
            <p className="text-2xl font-bold">{castleData.brickagy}%</p>
          </div>

          <div className="bg-[#1c1f2d] rounded-xl p-5 border border-gray-800/50">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-400 text-sm">Bricks Used</p>
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <FaCube className="text-blue-400" size={14} />
              </div>
            </div>
            <p className="text-2xl font-bold">{castleData.bricks_used}</p>
          </div>

          <div className="bg-[#1c1f2d] rounded-xl p-5 border border-gray-800/50">
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-400 text-sm">Top Donor</p>
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <FaTrophy className="text-amber-400" size={14} />
              </div>
            </div>
            <p className="text-lg font-bold truncate">{castleData.top_donor_name}</p>
            <p className="text-xs text-gray-500">{formatCurrency(castleData.top_donor_amount)} {terms.donation}</p>
          </div>
        </div>

        {/* Castle */}
        <CastleVisualization bricks={castleData.bricks} goal={castleData.castle_goal} />

        {/* Recent Bricks */}
        {castleData.bricks.length > 0 && (
          <div className="bg-[#1c1f2d] rounded-xl p-6 border border-gray-800/50 mt-6">
            <h3 className="font-semibold mb-1">Recent Brick Purchases</h3>
            <p className="text-xs text-gray-500 mb-4">Latest contributions to the castle</p>
            <div className="space-y-3">
              {[...castleData.bricks].reverse().slice(0, 10).map((brick, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-800/50 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      brick.tier === 'title' ? 'bg-amber-500/10' :
                      brick.tier === 'premium' ? 'bg-purple-500/10' : 'bg-blue-500/10'
                    }`}>
                      <FaCube className={tierColor(brick.tier)} size={12} />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{brick.donor_name}</p>
                      <p className="text-xs text-gray-500">{brick.message || terms.Donation}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-semibold ${tierColor(brick.tier)}`}>{formatCurrency(brick.amount)}</p>
                    <p className="text-xs text-gray-500">{formatDate(brick.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
